import { PenIcon } from "@src/assets/general-icons/PenIcon";
import { pricingActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import _ from "lodash";
import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { Button, ButtonVariant } from "../button/Button";
import { CommonModal } from "../modal/CommonModal";
import { IModalHandle, Modal } from "../modal/Modal";
import { clx } from "../utils/stringUtils";
import Checkbox from "../vGrid/Checkbox";
import styles from "./ExternalNotes.module.scss";
import XIconThemed from "@src/assets/general-icons/XIconThemed";
import { MixpanelCategory, mixpanelTrack } from "@src/components/utils/mixPanelUtils";
import { DeliveryClassificationModal } from "@src/components/deliveryClassification/DeliveryClassificationModal";
import { DeliveryClassification, externalNotesChanged } from "@src/utils/deliveryClassification";

export const ExternalNotes: React.FC<{
	wrapperStyle?: CSSProperties;
	notesText: string;
	itemID: number;
	listingId: number;
	tablePreview?: boolean;
}> = ({ wrapperStyle, notesText, itemID, listingId, tablePreview }) => {
	const [pageState, setPageState] = useState({
		mostUsedNotes: ["Mobile XFER", "Mobile QR", "Obstructed View", "Aisle Seats", "Side View"],
		moreNotes: [
			"Limited View",
			"Rear View",
			"Partial View",
			"Wheelchair Accessible",
			"AXS Mobile ID",
			"FLASH",
			"Paperless Tickets",
			"Parking Pass Only",
			"Local Pickup Only",
			"Standing Room Only",
			"21+ Event",
			"Will Call Only",
			"No View",
			"Vaccination Required",
		],
		selectedNotes: [] as Array<string>,
		tempSelectedNotes: [] as Array<string>,
		notes: [] as Array<string>,
		notesText: notesText || "",
		additional: "",
	});

	const modalRef = useRef<IModalHandle>(null);
	const classificationModalRef = useRef<IModalHandle>(null);
	const [pendingNotes, setPendingNotes] = useState<{ externalNotes: string; onSaved: () => void } | null>(null);
	const [classificationError, setClassificationError] = useState<string | null>(null);

	const dispatch = useAppDispatch();
	const appendListingNotes = useAppSelector((state) => state.pricing.appendListingNotes);
	const updateListingExternalNotes = useAppSelector((state) => state.pricing.updateListingExternalNotes);
	const eventInventoryAll = useAppSelector((state) => state.pricing.eventInventoryAll);
	const globalInventoryData = useAppSelector((state) => state.pricing.globalInventory.data);

	const normalize = (value: string) => value.trim().toLowerCase().replace(/\s+/g, " ");

	useEffect(() => {
		setPageState((prev) => ({
			...prev,
			notesText: notesText || "",
		}));
	}, [notesText]);

	useEffect(() => {
		const allNotes = [...pageState.mostUsedNotes, ...pageState.moreNotes];

		const parsed = (pageState.notesText || "")
			.split(/[;,]/)
			.map((x) => x.trim())
			.filter(Boolean);

		const selected = _.uniq(
			parsed.map((item) => allNotes.find((note) => normalize(note) === normalize(item))).filter(Boolean) as Array<string>,
		);

		const additional = _.uniq(parsed.filter((item) => !allNotes.some((note) => normalize(note) === normalize(item))));

		setPageState((prev) => ({
			...prev,
			selectedNotes: [...selected, ...additional],
			tempSelectedNotes: [...selected, ...additional],
			notes: allNotes,
			additional: additional.join(", "),
		}));
	}, [pageState.notesText]);

	const buildNotes = () => {
		const baseNotes = _.uniq(
			pageState.tempSelectedNotes.filter((item) => pageState.notes.some((note) => normalize(note) === normalize(item))),
		);

		const additionalNotes = pageState.additional
			.split(/[;,]/)
			.map((x) => x.trim())
			.filter(Boolean);

		const cleanedAdditional = _.uniq(
			additionalNotes.filter((item) => !baseNotes.some((note) => normalize(note) === normalize(item))),
		);

		const combined = [...baseNotes, ...cleanedAdditional];
		return { combined, cleanedAdditional, externalNotes: combined.join(", ") };
	};

	const requestClassification = (externalNotes: string, onSaved: () => void) => {
		setClassificationError(null);
		setPendingNotes({ externalNotes, onSaved });
		classificationModalRef.current?.openModal();
	};

	const handleClassifiedSave = async (deliveryClassification: DeliveryClassification) => {
		if (!pendingNotes) return;
		setClassificationError(null);
		try {
			await dispatch(
				pricingActions.updateListingExternalNotes({
					listingIds: [listingId],
					externalNotes: pendingNotes.externalNotes,
					appendNotes: false,
					deliveryClassification,
					skipCache: true,
					skipSavingCache: true,
				}),
			).unwrap();
			setPageState((prev) => ({ ...prev, notesText: pendingNotes.externalNotes }));
			classificationModalRef.current?.closeModal();
			pendingNotes.onSaved();
			setPendingNotes(null);
		} catch (error) {
			setClassificationError(typeof error === "string" && error ? error : "Couldn't save the external notes.");
		}
	};

	const handleSave = async () => {
		const { combined, cleanedAdditional, externalNotes } = buildNotes();

		setPageState((prev) => ({
			...prev,
			notesText: externalNotes,
			selectedNotes: combined,
			tempSelectedNotes: combined,
			additional: cleanedAdditional.join(", "),
		}));

		const dataToAdd = {
			internalNotes: null,
			externalNotes,
			listingIds: [listingId],
		};

		try {
			await dispatch(pricingActions.appendListingNotes(dataToAdd)).unwrap();

			const newData = eventInventoryAll.data.map((event) => {
				if (event.Item_ID != itemID) return event;
				return {
					...event,
					External_Notes: externalNotes,
				};
			});

			dispatch(pricingActions.updateInventoryAll({ data: newData }));

			if (globalInventoryData.length) {
				const newGlobalData = globalInventoryData.map((item) => {
					return item.Listing_ID === listingId ? { ...item, External_Notes: externalNotes } : item;
				});
				dispatch(pricingActions.updateGlobalInventory({ data: newGlobalData }));
			}
		} catch (error) {
			console.log(error);
		}
	};

	const renderOption = (x: string) => {
		const noteIncluded = pageState.tempSelectedNotes.some((note) => normalize(note) === normalize(x));

		return (
			<label key={x} className={clx(styles.option, noteIncluded ? styles.checked : undefined)}>
				<Checkbox
					isChecked={noteIncluded}
					controlSize="m"
					onChange={(isChecked) => {
						setPageState((prev) => {
							const withoutCurrent = prev.tempSelectedNotes.filter((note) => normalize(note) !== normalize(x));

							return {
								...prev,
								tempSelectedNotes: isChecked ? [...withoutCurrent, x] : withoutCurrent,
							};
						});
					}}
				/>
				{x}
			</label>
		);
	};

	const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
		event.preventDefault();
		const text = event.clipboardData.getData("text");
		const sanitizedText = text.replace(/\r?\n|\r/g, ", ");

		const { selectionStart, selectionEnd } = event.currentTarget;
		const value = event.currentTarget.value;

		const newValue = value.slice(0, selectionStart) + sanitizedText + value.slice(selectionEnd);

		setPageState((prev) => ({
			...prev,
			additional: newValue,
		}));
	};

	const handleExternalNotes = (event?: React.MouseEvent) => {
		event?.stopPropagation();
		modalRef.current?.openModal();
		mixpanelTrack(MixpanelCategory.ADD_EDIT_EXTERNAL_NOTES);
	};

	const handleAdditionalBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
		const baseNotes = _.uniq(
			pageState.tempSelectedNotes.filter((item) => pageState.notes.some((note) => normalize(note) === normalize(item))),
		);

		const additionalNotes = e.target.value
			.split(/[;,]/)
			.map((x) => x.trim())
			.filter(Boolean);

		const cleanedAdditional = _.uniq(
			additionalNotes.filter((item) => !baseNotes.some((note) => normalize(note) === normalize(item))),
		);

		const combined = [...baseNotes, ...cleanedAdditional];

		setPageState((prev) => ({
			...prev,
			notesText: combined.join(", "),
			additional: cleanedAdditional.join(", "),
			tempSelectedNotes: combined,
			selectedNotes: combined,
		}));
	};

	const renderExternalNotesModal = (showCustomButton: boolean) => (
		<Modal
			ref={modalRef}
			customButton={
				showCustomButton ? (
					<Button
						controlSize="sm"
						onClick={(e) => handleExternalNotes(e)}
						style={{ whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: "3px" }}
						variant={ButtonVariant.Secondary}>
						<PenIcon />
						Edit
					</Button>
				) : (
					<></>
				)
			}>
			<CommonModal
				controlSize="m"
				closeModal={modalRef.current?.closeModal}
				title="External Notes"
				content={
					<div className={styles.main}>
						<div className={styles.header}>Most used</div>
						<div className={styles.options}>{pageState.mostUsedNotes.map(renderOption)}</div>

						<div className={styles.header}>More options</div>
						<div className={styles.options}>{pageState.moreNotes.map(renderOption)}</div>

						<div className={styles.header}>Additional Comments</div>
						<div className={styles.additional}>
							<textarea
								className={styles.textArea}
								value={pageState.additional}
								placeholder="Example: note1, note2, note3"
								onBlur={handleAdditionalBlur}
								onChange={(e) =>
									setPageState((prev) => ({
										...prev,
										additional: e.target.value,
									}))
								}
								onPaste={handlePaste}
								onKeyDown={(e) => {
									if (e.key === "Enter") e.preventDefault();
								}}
							/>
						</div>
					</div>
				}
				buttons={
					<div className={styles.buttonsContainer}>
						<Button
							onClick={() => modalRef.current?.closeModal()}
							disabled={appendListingNotes.loading}
							variant={ButtonVariant.Secondary}>
							Close
						</Button>

						<Button
							isLoading={appendListingNotes.loading}
							onClick={async () => {
								const { externalNotes } = buildNotes();
								if (externalNotesChanged(pageState.notesText, externalNotes)) {
									requestClassification(externalNotes, () => modalRef.current?.closeModal());
									return;
								}
								await handleSave();
								modalRef.current?.closeModal();
							}}
							variant={ButtonVariant.Primary}>
							Submit
						</Button>
					</div>
				}
			/>
		</Modal>
	);

	const classificationModal = (
		<Modal ref={classificationModalRef}>
			{pendingNotes && (
				<DeliveryClassificationModal
					externalNotes={pendingNotes.externalNotes}
					saving={updateListingExternalNotes.loading}
					error={classificationError}
					onCancel={() => {
						classificationModalRef.current?.closeModal();
						setPendingNotes(null);
					}}
					onSave={handleClassifiedSave}
				/>
			)}
		</Modal>
	);

	if (tablePreview) {
		return (
			<div>
				<div style={{ cursor: "pointer" }} onClick={(e) => handleExternalNotes(e)} title="Click to edit external notes">
					{notesText || <span className={styles.emptyNote}>No note yet</span>}
				</div>

				{renderExternalNotesModal(false)}
				{classificationModal}
			</div>
		);
	}

	return (
		<div className={styles.main} style={wrapperStyle}>
			<div className={styles.headline}>External Notes</div>

			<div className={styles.content}>
				<div className={styles.notes}>
					{pageState.selectedNotes.length === 0 && <div className={styles.emptyNote}>No note yet</div>}

					{pageState.selectedNotes.map((x) => (
						<div key={x} className={styles.note}>
							{x}

							<Button
								isLoading={appendListingNotes.loading}
								className={styles.removeBtn}
								controlSize="sm"
								variant={ButtonVariant.Secondary}
								style={{ minHeight: "10px" }}
								onClick={async () => {
									const newTempSelectedNotes = pageState.tempSelectedNotes.filter(
										(note) => normalize(note) !== normalize(x),
									);

									const newSelectedNotes = pageState.selectedNotes.filter((note) => normalize(note) !== normalize(x));

									const newAdditional = newSelectedNotes.filter(
										(item) => !pageState.notes.some((note) => normalize(note) === normalize(item)),
									);

									const externalNotes = newSelectedNotes.join(", ");

									if (externalNotesChanged(pageState.notesText, externalNotes)) {
										requestClassification(externalNotes, () => undefined);
										return;
									}

									setPageState((prev) => ({
										...prev,
										tempSelectedNotes: newTempSelectedNotes,
										selectedNotes: newSelectedNotes,
										notesText: externalNotes,
										additional: newAdditional.join(", "),
									}));

									const dataToAdd = {
										internalNotes: null,
										externalNotes,
										listingIds: [listingId],
									};

									try {
										await dispatch(pricingActions.appendListingNotes(dataToAdd)).unwrap();
									} catch (error) {
										console.log(error);
									}
								}}>
								<XIconThemed />
							</Button>
						</div>
					))}
				</div>

				<div className={styles.editContainer}>{renderExternalNotesModal(true)}</div>
			</div>
			{classificationModal}
		</div>
	);
};
